/**
 * Money, as the rules require it written and as the services publish it.
 *
 * `CLAUDE.md` section 2: _"Money is always shown with the currency code and full minor units (for
 * example, KES 1,184,000.00)."_ So `KES 2,100.00` and never `KES 2.1K`, never `2,100`, and never a
 * number without its code.
 *
 * Two things are never done here, and both are platform rules rather than preferences:
 *
 *   - **Minor units are never divided into a float.** `AGENTS.md`: never use floating-point values
 *     for money. The integer is split by string arithmetic, so 4_499_999_999_999 formats exactly
 *     where `/ 100` would already have lost it.
 *   - **An amount is never shown without the currency it arrived with.** The two travel together in
 *     every published payload on this platform, and separating them is how a rider reads a TZS
 *     figure as a KES one. EXP-002's `money()` refuses to build one without the other for the same
 *     reason, so an absence arrives here as an absence rather than as a zero.
 *
 * ## The one place this disagrees with the design, and why the rule wins
 *
 * `10 Rider App.dc.html` writes every figure with no decimal places: `KES 2,100 · XL`,
 * `KES 1,450`, `Final fare KES 2,290`, `+ KES 40`. `CLAUDE.md` section 2 asks for full minor units.
 * Those two cannot both be satisfied, and section 2 is a platform rule about money where the
 * design's shorter form is a sample value, so the rule is followed and the difference is recorded as
 * a design question rather than decided silently. It is also the **largest single measured
 * difference on the three fare screens**, because the fare box sets its figure at 20px and 800
 * weight — three extra glyphs there move nothing but are three glyphs wide. It is reported as a
 * number rather than closed by dropping the decimals, which is the same call
 * `apps/driver-app/src/money.ts` made and for the same reason.
 */

/**
 * How many minor units make one major unit, per currency.
 *
 * ISO 4217 exponents, and only for the currencies this platform's own contracts and fixtures name.
 * An unknown code falls back to two, which is the exponent of all but about twenty of the world's
 * currencies — and the fallback is reported by `minorUnitsAreAssumed` rather than hidden, because a
 * JPY amount formatted with two decimals is wrong by a hundredfold and a rider would not know.
 */
const MINOR_UNIT_EXPONENTS: Readonly<Record<string, number>> = {
  // East Africa, which is where this product operates.
  KES: 2,
  TZS: 2,
  UGX: 0,
  RWF: 0,
  ETB: 2,
  // The settlement currencies the finance services name.
  USD: 2,
  EUR: 2,
  GBP: 2,
  // Zero-decimal currencies that are commonly got wrong.
  JPY: 0,
  KRW: 0,
  VND: 0,
  ISK: 0,
  CLP: 0,
  XOF: 0,
  XAF: 0,
  // Three-decimal currencies, the other direction of the same mistake.
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
};

export function minorUnitExponent(currency: string): number {
  return MINOR_UNIT_EXPONENTS[currency.toUpperCase()] ?? 2;
}

/** True when the exponent above was a fallback rather than a known one. */
export function minorUnitsAreAssumed(currency: string): boolean {
  return MINOR_UNIT_EXPONENTS[currency.toUpperCase()] === undefined;
}

/** The shape every figure on this application travels in, matching EXP-002's own `Money`. */
export interface Money {
  readonly currency: string;
  readonly amountMinor: number;
}

/**
 * `KES 2,100.00`, from an integer number of minor units and a currency code.
 *
 * Grouped in threes with a comma and separated by a full stop, which is what the design writes and
 * what the rule's own example writes. Negative amounts take an en dash and a space rather than a
 * hyphen, which is the glyph the design uses.
 */
export function formatMoney(amountMinor: number, currency: string): string {
  if (!Number.isInteger(amountMinor)) {
    throw new TypeError(`money must be whole minor units, got ${String(amountMinor)}`);
  }
  const code = currency.toUpperCase();
  const exponent = minorUnitExponent(code);
  const negative = amountMinor < 0;
  const digits = String(Math.abs(amountMinor)).padStart(exponent + 1, "0");
  const major = digits.slice(0, digits.length - exponent);
  const minor = exponent === 0 ? "" : `.${digits.slice(digits.length - exponent)}`;
  const grouped = major.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "– " : ""}${code} ${grouped}${minor}`;
}

/** The same, from the object shape EXP-002 publishes. */
export function money(value: Money): string {
  return formatMoney(value.amountMinor, value.currency);
}

/**
 * The same again, from the shape MOB-02 publishes, which is not the same shape.
 *
 * A quote's fare is `{ currency, totalMinor }`; EXP-002's money is `{ currency, amountMinor }`. Two
 * names for one integer number of minor units, and the difference is real rather than cosmetic: the
 * application reads MOB-02 directly on `2 Choose` and `3 Quote`, and reads EXP-002 on the seven
 * journey screens, so both shapes arrive here.
 *
 * Converted through one function rather than spread at the call sites, because a `.totalMinor` read
 * as `.amountMinor` is `undefined`, and `formatMoney(undefined)` throws where a silent zero would
 * have been worse. Recorded as a design question: the platform has two names for one concept, and
 * the right fix is upstream rather than a wider type here.
 */
export function quoteMoney(fare: {
  readonly currency: string;
  readonly totalMinor: number;
}): string {
  return formatMoney(fare.totalMinor, fare.currency);
}

/**
 * A wallet's spendable amount, from FIN-03's `netDebitMinor`.
 *
 * **This is the one place this application performs an accounting conversion, and it is here rather
 * than at a call site so there is one of it to be wrong.**
 *
 * FIN-03 publishes `netDebitMinor` — `debitMinor - creditMinor`, the ledger's own sign, debits
 * positive — and deliberately does not publish an "available balance". ADR 0041 §5 says why: nothing
 * on this platform publishes a chart of accounts, so no service, FIN-03 included, knows which side an
 * account's normal balance sits on. A service that renamed the field would be asserting an
 * accounting fact it cannot establish.
 *
 * So the assumption is made here, and it is an assumption about **a rider's own wallet account** and
 * about nothing else. The evidence for it is the design's own movement signs rather than a
 * convention taken on trust:
 *
 *   - `14 WP13 Money and Account.dc.html` draws `Top-up · M-Pesa · +KES 1,000` and
 *     `Trip trp_8F2K · – KES 980` on the same screen as `KES 1,240.00`. A top-up is a plus.
 *   - A top-up **credits** the rider's wallet account: the platform now owes the rider more, which
 *     on the platform's books is a credit to a liability. A trip debits it.
 *   - So credit is positive on this screen, and the spendable amount is `creditMinor - debitMinor`,
 *     which is `-netDebitMinor`.
 *
 * **Two guards hold it, and the measured one is the opposite of the obvious one.** The assumption
 * was reintroduced on purpose to find out:
 *
 *   - `apps/rider-app/tests/money-screens.test.tsx` fails **by name**, on two cases.
 *   - the acceptance comparison moves `Wallet · light` at 320 from **2.47% to 2.68%** — about 470
 *     pixels, which is an en dash and a space.
 *
 * So the sentence this comment used to carry — that the pixel check would catch it loudly — was
 * wrong, and measuring it is how that was found. A sign flip is loud to a person looking at the
 * diff image and nearly silent in the number, which is the same lesson the stylesheet guard records
 * from the other direction: a one-digit colour change moves no pixel at all. **The named test is
 * the guard here and the capture is the confirmation**, not the reverse. Q-146 records the
 * assumption and what would change it: a published account type, or a wallet that names the ledger
 * account it corresponds to.
 */
export function walletBalanceMinor(netDebitMinor: number): number {
  return -netDebitMinor;
}

/**
 * `+ KES 1,000` or `– KES 980` — one movement, signed from the rider's own side of the posting.
 *
 * The same convention as `walletBalanceMinor` and the same evidence: a credit to the rider's account
 * is money in. A transaction may touch the account more than once — a correction and its reversal
 * can both land on one account in one posting — so the lines for this account are summed rather than
 * the first one taken, which is also what makes a balanced correction read as zero rather than as
 * whichever line came first.
 */
export function movementMinorFor(
  accountId: string,
  lines: readonly {
    readonly accountId: string;
    readonly side: string;
    readonly amountMinor: number;
  }[],
): number {
  let total = 0;
  for (const line of lines) {
    if (line.accountId !== accountId) continue;
    total += line.side === "credit" ? line.amountMinor : -line.amountMinor;
  }
  return total;
}

/**
 * `+KES 1,000.00` or `– KES 980.00` — one wallet movement, as `14 WP13` writes its rows.
 *
 * **Not `signedMoney`, and the difference is one space.** `10 Rider App.dc.html` writes a fare
 * breakdown line as `+ KES 40` and `14 WP13 Money and Account.dc.html` writes a wallet movement as
 * `'+KES 1,000'` — no space after the plus — while writing the negative as `'– KES 980'` with one.
 * Both were read from the design sources rather than normalised to each other, because `CLAUDE.md`
 * section 1 asks for every value as written and two design files are allowed to disagree. A single
 * helper would have moved one glyph on one of the two screens, which is the class of difference the
 * acceptance comparison is there to find.
 *
 * The negative keeps `formatMoney`'s own en dash and space; only the plus is added here.
 */
export function walletMovementMoney(amountMinor: number, currency: string): string {
  const written = formatMoney(amountMinor, currency);
  return amountMinor < 0 ? written : `+${written}`;
}

/**
 * `+ KES 40` — one line of a fare breakdown, as `8b Fare differs` writes its rows.
 *
 * The sign is the design's and is drawn for a positive amount too: a breakdown whose lines are all
 * additions still writes the `+`, because a rider reading `KES 40` beside `Waiting 9 min` cannot
 * tell a charge from a credit. A negative amount keeps `formatMoney`'s en dash and loses the `+`.
 */
export function signedMoney(value: Money): string {
  return value.amountMinor < 0 ? money(value) : `+ ${money(value)}`;
}
